import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { ChartBlockPayload } from "@/features/assistant/types";
import {
  chartTooltipContentStyle,
  chartTooltipLabelStyle,
} from "@/lib/chart-tooltip";

const COLORS = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#be185d",
  "#4b5563",
];

const formatNumber = (v: unknown) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
    : String(v ?? "");

export function BlockChart({ payload }: { payload: ChartBlockPayload }) {
  const { kind, x_key: xKey, series, rows } = payload;

  if (kind === "pie") {
    const s = series[0];
    return (
      <div className="h-[280px] w-full" data-testid="assistant-block-chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey={s.key}
              nameKey={xKey}
              outerRadius={100}
              label={({ name }) => String(name)}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={formatNumber}
              contentStyle={chartTooltipContentStyle}
              labelStyle={chartTooltipLabelStyle}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const Chart = kind === "line" ? LineChart : BarChart;
  return (
    <div className="h-[280px] w-full" data-testid="assistant-block-chart">
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={formatNumber}
            width={56}
          />
          <Tooltip
            formatter={formatNumber}
            contentStyle={chartTooltipContentStyle}
            labelStyle={chartTooltipLabelStyle}
          />
          {series.length > 1 && <Legend />}
          {series.map((s, i) =>
            kind === "line" ? (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
              />
            ) : (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={COLORS[i % COLORS.length]}
              />
            ),
          )}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}
